import React from 'react';
import { Building2Icon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import type { Division, MetricType, PeriodInfo } from '../../types';
import { ProgressBar } from '../ui/ProgressBar';
import { Badge } from '../ui/Badge';
import { TeamSection } from './TeamSection';
import { aggregateDivisionMetrics, getProgressPercentage, getProgressStatus, getProgressBorderColor, calculateProjection } from '../../utils/calculations';
interface DivisionSectionProps {
  division: Division;
  period: PeriodInfo;
  metric: MetricType;
  expanded: boolean;
  onToggle: () => void;
}
export function DivisionSection({
  division,
  period,
  metric,
  expanded,
  onToggle
}: DivisionSectionProps) {
  const {
    current,
    goal
  } = aggregateDivisionMetrics(division, metric);
  const percentage = getProgressPercentage(current, goal);
  const projected = calculateProjection(current, period.daysElapsed, period.totalDays);
  const projPct = getProgressPercentage(projected, goal);
  const status = getProgressStatus(projPct);
  const borderColor = getProgressBorderColor(percentage);
  return <section className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden`} data-id="element-19">
      {/* Division Header */}
      <button onClick={onToggle} className={`w-full flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors border-l-4 ${borderColor}`} aria-expanded={expanded} aria-label={`${division.name} — ${expanded ? 'collapse' : 'expand'}`} data-id="element-20">
        {/* Chevron */}
        <div className="flex-shrink-0 text-gray-400" data-id="element-21">
          {expanded ? <ChevronDownIcon className="w-5 h-5" data-id="element-22" /> : <ChevronRightIcon className="w-5 h-5" data-id="element-23" />}
        </div>

        {/* Division Info */}
        <div className="flex items-center gap-3 w-48 flex-shrink-0" data-id="element-24">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-25">
            <Building2Icon className="w-4.5 h-4.5 text-slate-600" data-id="element-26" />
          </div>
          <div className="text-left min-w-0" data-id="element-27">
            <p className="text-sm font-bold text-gray-900 truncate" data-id="element-28">
              {division.name}
            </p>
            <p className="text-xs text-gray-500 truncate" data-id="element-29">
              Lead: {division.leadName}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 min-w-0" data-id="element-30">
          <ProgressBar current={current} goal={goal} size="md" showLabel={true} data-id="element-31" />
        </div>

        {/* Status Badge */}
        <div className="flex-shrink-0" data-id="element-32">
          <Badge status={status} size="md" data-id="element-33" />
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && <div className="px-5 pb-5 pt-2" data-id="element-34">
          {division.teams.map(team => <TeamSection key={team.id} team={team} period={period} metric={metric} data-id="element-35" />)}
        </div>}
    </section>;
}